/**
 * Soft≠Live Page66 — Ops map chrome Live desk (layers + map controls).
 * Soft≠Live Page216 — dual map_plane ∥ decision_plane via GET /ops/map/status.
 * Mount: /ops map surface (not a panel tab) — floating layers + mapctl.
 * Buses: useLive atoms for layer honesty; Soft fleet layer only on localhost.
 * Soft residual deleted:
 *  - bare "Map layers" sandpit without Live meta / Soft-off honesty
 *  - Soft fleet toggle on production www (never offered)
 *  - unlabeled mapctl without Live re-read / site meta rail
 *  - Soft HITL invent when decision plane sealed (Page216)
 */
import { html, M, Ico, FADE, stop, go } from "./ui.js";
import { regionOptionsOf } from "./ops-map-region-filter.js";
import { path, useApi } from "./api.js";
import { useLive } from "./live.js";
import { useSession } from "./session.js";
import { isLocalHost } from "./pathseal.js";
import { deviceSelIdOf } from "./ops-devices-desk.js";
import { writeAssignPin } from "./external-fleet-desk.js";
import { useState } from "react";
import { parseLatLngPair, geocodeOpenMeteo } from "../lib/ops-map-geocode.js";
import {
  OPS_MAP_MIN_ZOOM,
  OPS_MAP_DEFAULT_ZOOM_REGION,
  clampOpsMapZoom,
} from "../lib/ops-map-limits.js";
import { frameSoftFocus } from "./soft-fleet-glyphs.js";
import { operatorSay } from "./operator-say.js";

/** Soft≠Live Page216: missing contract ops must not crash map chrome. */
export function safeOpsMapPath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    if (op === "get__ops_map_status") return "/ops/map/status";
    if (op === "get__devices") return "/devices";
    if (op === "get__ai_home-context") return "/ai/home-context";
    return "";
  }
}

/** Soft seat copy from Live posture codes — never show internal plane keys. */
export function opsMapDetailLabel(detail) {
  const d = String(detail || "").trim();
  if (!d) return "";
  switch (d) {
    case "ops_map_decision_user_floor":
      return "Map open · approvals need a manager";
    case "ops_map_decision_staff_floor":
      return "Map open · decision actions closed for staff";
    case "ops_map_decision_sealed":
      return "Map open · decisions sealed";
    case "ops_map_geometry_sealed":
      return "Map closed for this seat";
    case "ops_map_all_sealed":
      return "Map and decisions closed";
    case "ops_map_status_loading":
      return "Confirming what this seat can open…";
    case "ops_map_status_error":
      return "Could not confirm map access · try Refresh";
    default:
      /* Never echo path-like residual into chrome. */
      if (/^\//.test(d) || /\bGET\b|\bPOST\b/i.test(d)) return "On site";
      return operatorSay(d, "On site");
  }
}

/** Pure: seat chips from Live Ops Map posture (Page216). Fail-closed on load/error. */
export function opsMapSeatOf(post, opts = {}) {
  const loading = !!opts.loading;
  const error = !!opts.error;
  if (error) {
    return {
      enabled: false,
      armed: false,
      mapAllowed: false,
      teamLocationsAllowed: false,
      decisionAllowed: false,
      hitlAllowed: false,
      reReadAllowed: true,
      softFleetLocalOnly: false,
      role: String(opts.role || ""),
      detail: "ops_map_status_error",
      detailLabel: opsMapDetailLabel("ops_map_status_error"),
      devicePaintMax: 400,
    };
  }
  if (loading) {
    return {
      enabled: false,
      armed: false,
      mapAllowed: false,
      teamLocationsAllowed: false,
      decisionAllowed: false,
      hitlAllowed: false,
      reReadAllowed: false,
      softFleetLocalOnly: false,
      role: String(opts.role || ""),
      detail: "ops_map_status_loading",
      detailLabel: opsMapDetailLabel("ops_map_status_loading"),
      devicePaintMax: 400,
    };
  }
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const paint = Number(lim.device_paint_max);
  const mapAllowed = p.map_plane_allowed === true;
  const decisionAllowed = p.decision_plane_allowed === true;
  const detail = p.detail || null;
  return {
    enabled: p.enabled === true,
    armed: p.armed === true,
    /* Fail-closed: only explicit Live true unlocks (no “missing field ⇒ open”). */
    mapAllowed,
    teamLocationsAllowed: p.team_locations_allowed === true,
    decisionAllowed,
    hitlAllowed: p.hitl_allowed === true || decisionAllowed,
    reReadAllowed: p.re_read_allowed === true,
    softFleetLocalOnly: p.soft_fleet_local_only === true,
    role: String(p.principal_role || opts.role || ""),
    detail,
    detailLabel: opsMapDetailLabel(detail),
    devicePaintMax:
      Number.isFinite(paint) && paint > 0 ? Math.floor(paint) : 400,
  };
}

/** Hook: always-on Ops Map dual posture. */
export function useOpsMapSeat() {
  const { me } = useSession();
  const role = String((me && me.role) || "").toLowerCase();
  const posture = useApi(safeOpsMapPath("get__ops_map_status"));
  const post = posture.data || {};
  const seat = opsMapSeatOf(post, {
    loading: !!posture.loading && !posture.data,
    error: !!posture.error && !posture.data,
    role,
  });
  return { posture, seat, role };
}

/** Pure: lat/lng from map centre — never invent Soft coords. */
export function mapCenterPinOf(map) {
  if (!map || typeof map.getCenter !== "function") return null;
  try {
    const c = map.getCenter();
    const lat = Number(c && c.lat);
    const lng = Number(c && c.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  } catch (_) {
    return null;
  }
}

/** Pure: lat/lng from maplibre contextmenu / click event. */
export function mapEventPinOf(e) {
  const ll = e && e.lngLat;
  if (!ll) return null;
  const lat = Number(ll.lat);
  const lng = Number(ll.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

/**
 * Live store pin for External / SEB intent desk (session). Soft “unit moved” still deleted.
 * @returns {boolean} whether pin was stored
 */
export function assignExternalPinLive(pin, toast, source) {
  if (!pin) {
    toast && toast("No coordinates on the map yet", "warn");
    return false;
  }
  writeAssignPin(pin.lat, pin.lng, null, { handoff: true });
  toast && toast("External destination saved · open Robots to dispatch", "go");
  return true;
}

/** Pure: Live layer rows for production + Soft band when local. */
export function mapLayerRowsOf(localSoft) {
  const rows = [
    ["fence", "Area fences", "#3DE1C8"],
    ["mesh", "Group links", "#E9EFEE"],
    ["offline", "Quiet machines", "#6B7A78"],
    ["people", "People roster", "#A78BFA"],
    ["trail", "Trails history", "#7C6BE8"],
  ];
  if (localSoft) rows.push(["softFleet", "Practice fleet (this PC)", "#7EC8B8"]);
  return rows;
}

/** Pure: site chip KPIs for map rail. */
export function mapChromeKpisOf(live, peopleRows) {
  /* Fail-closed: until devicesReady, paint empty — never treat a stale parent cache as Live fleet. */
  const ready = !!(live && live.devicesReady);
  const devices = ready ? ((live && live.devices) || []) : [];
  const offline = ready ? ((live && live.offline) || []) : [];
  const alarms = (live && live.alarms) || [];
  const people = Array.isArray(peopleRows) ? peopleRows : ((live && live.people) || []);
  const total = devices.length;
  const online = Math.max(0, total - offline.length);
  const peoplePos = people.filter((p) => p && p.ll).length;
  return {
    total,
    online,
    openAlarms: alarms.length,
    people: people.length,
    peoplePos,
    zones: Array.isArray(live && live.zones) ? live.zones.length : 0,
    ready,
    softBound: (live && live.soft && live.soft.bound) || 0,
  };
}

/**
 * Floating layers sheet — Live honesty · Soft fleet only when localSoft.
 */
export function OpsMapLayersDesk({
  layers,
  setLayers,
  onClose,
  localSoft,
  toast,
  seat,
  peopleLive,
  zones,
  devices,
  regionIds,
  onToggleRegion,
  sites,
  siteIds,
  onToggleSite,
  autoLive,
  autoLivePref,
  onAutoLive,
  custodyMode,
}) {
  const live = useLive();
  const kpis = mapChromeKpisOf(live, peopleLive);
  const mapOn = seat ? !!seat.mapAllowed : true;
  const rows = mapLayerRowsOf(!!localSoft && mapOn);
  const regionRows = regionOptionsOf(zones, devices);
  const picked = new Set(Array.isArray(regionIds) ? regionIds.map(String) : []);
  const siteRows = (Array.isArray(sites) ? sites : []).map((s) => ({
    id: String((s && (s.site_id || s.id)) || "").trim(),
    label: String((s && (s.label || s.name)) || (s && (s.site_id || s.id)) || "").trim(),
  })).filter((s) => s.id);
  const sitePicked = new Set(Array.isArray(siteIds) ? siteIds.map(String) : []);
  const custody = String(custodyMode || "");
  const followsCustody = (autoLivePref == null || autoLivePref === "") && custody === "AUTO";

  const reRead = () => {
    if (seat && !seat.reReadAllowed) {
      toast && toast("Refresh is not available right now.", "warn");
      return;
    }
    if (live && typeof live.reload === "function") live.reload();
    toast && toast("Map data refreshed", "go");
  };

  return html`
    <${M.div} className="layers glass ops-map-layers"
        data-cmc-page="page66-layers" data-cmc-residual="page216-ops-map"
        initial=${{ x: -14, opacity: 0 }} animate=${{ x: 0, opacity: 1 }}
        exit=${{ x: -10, opacity: 0 }} transition=${FADE} onClick=${stop}>
      <div className="layers__in ops-map-layers__in">
        <div className="ops-map-layers__head">
          <div>
            <p className="eyebrow">Map layers</p>
            <h3 className="ops-map-layers__title">What you see on the map</h3>
          </div>
          <button type="button" className="x" onClick=${onClose} title="Close layers"
            aria-label="Close layers"><${Ico} d="close" w=${13}/></button>
        </div>

        ${seat ? html`<p className="note" style=${{ margin:"0 0 8px" }}>
          ${seat.mapAllowed ? "Map open" : "Map closed"}
          · ${seat.decisionAllowed ? "Decisions open" : "Decisions closed"}
          ${seat.detailLabel ? " · " + seat.detailLabel : ""}
        </p>` : null}

        <div className="ops-map-layers__kpis" role="group" aria-label="Map register KPIs">
          <div className="ops-map-layers__kpi" data-on=${kpis.openAlarms ? "1" : "0"}>
            <span>Alarms</span><b>${mapOn && kpis.ready ? kpis.openAlarms : (mapOn ? "…" : "—")}</b>
          </div>
          <div className="ops-map-layers__kpi" data-on=${kpis.online ? "1" : "0"}>
            <span>Nodes</span><b>${mapOn && kpis.ready ? (kpis.online + "/" + kpis.total) : (mapOn ? "…" : "—")}</b>
          </div>
          <div className="ops-map-layers__kpi" data-on=${kpis.peoplePos ? "1" : "0"}>
            <span>People fix</span><b>${mapOn ? (kpis.peoplePos + "/" + kpis.people) : "—"}</b>
          </div>
        </div>
        ${mapOn && kpis.peoplePos === 0 ? html`<p className="note" data-cmc-people-empty="1">
          ${seat && seat.teamLocationsAllowed === false
            ? "Team locations stay off on this deployment. Board pins on the map stay separate."
            : "No shared phone locations yet. When responders share GPS, they appear here and on the map — stale points stay labeled."}
        </p>` : null}

        <div className="btn-row" style=${{ margin: "0 0 10px" }}>
          <button type="button" className="btn btn--ghost btn--sm"
            disabled=${seat && !seat.reReadAllowed}
            onClick=${reRead}>
            <${Ico} d="refresh" w=${13}/> Refresh map data
          </button>
        </div>

        ${typeof onToggleSite === "function" && siteRows.length ? html`
        <p className="ops-map-layers__label">Sites <small>· none picked = every site</small></p>
        <div className="ops-map-layers__regions" role="group" aria-label="Site filter" data-cmc-site-filter="1"
          data-count=${String(sitePicked.size)}>
          <button type="button" className="chip" data-sel=${sitePicked.size === 0 ? "1" : "0"}
            onClick=${() => { onToggleSite(null); toast && toast("Every site", "go"); }}>All</button>
          ${siteRows.map((s) => {
            const on = sitePicked.has(s.id);
            return html`<button key=${s.id} type="button" className="chip" data-sel=${on ? "1" : "0"}
              aria-pressed=${on ? "true" : "false"}
              onClick=${() => onToggleSite(s.id)}>${s.label || s.id}</button>`;
          })}
        </div>` : null}

        ${typeof onToggleRegion === "function" ? html`
        <p className="ops-map-layers__label">Regions <small>· none picked = whole map</small></p>
        <div className="ops-map-layers__regions" role="group" aria-label="Region filter" data-cmc-region-filter="1"
          data-count=${String(picked.size)}>
          <button type="button" className="chip" data-sel=${picked.size === 0 ? "1" : "0"}
            onClick=${() => { onToggleRegion(null); toast && toast("Whole map", "go"); }}>All</button>
          ${regionRows.map((z) => {
            const id = String(z.id || z.label);
            const on = picked.has(id) || picked.has(String(z.label || ""));
            return html`<button key=${id} type="button" className="chip" data-sel=${on ? "1" : "0"}
              aria-pressed=${on ? "true" : "false"}
              onClick=${() => onToggleRegion(id)}>${z.label || id}</button>`;
          })}
          ${!regionRows.length ? html`<span className="note">No regions on this site yet.</span>` : null}
        </div>` : null}

        ${typeof onAutoLive === "function" ? html`
        <button type="button" className="lay" data-cmc-auto-live="1"
          data-on=${autoLive ? "1" : "0"}
          data-cmc-auto-live-pref=${autoLivePref === "1" || autoLivePref === "0" ? autoLivePref : "custody"}
          disabled=${!mapOn}
          onClick=${() => { onAutoLive(!autoLive); toast && toast(autoLive ? "Auto live off" : "Auto live on signal", "go"); }}>
          <span className="sw"><i/></span>Live window on signal
          <span className="key"><s style=${{ background: "#3DE1C8" }}/></span>
        </button>
        <p className="layers__note">${followsCustody
          ? "AI custody is AUTO, so a new signal opens the window. Turn this off to keep it closed."
          : "Opens a small live window on the map for new signals in the picked regions. Needs a live grant."}</p>
        ${custody ? html`<p className="layers__note" data-cmc-ai-custody=${custody}>
          AI custody · <b>${custody}</b> · set in Settings · AI
        </p>` : null}` : null}

        <p className="ops-map-layers__label">Map layers</p>
        ${rows.map(([k, l, c]) => html`
          <button key=${k} type="button" className="lay"
            data-on=${layers[k] ? "1" : "0"} data-soft=${k === "softFleet" ? "1" : "0"}
            onClick=${() => setLayers((x) => ({ ...x, [k]: !x[k] }))}>
            <span className="sw"><i/></span>${l}
            <span className="key"><s style=${{ background: c }}/></span>
          </button>`)}

        ${localSoft
          ? html`<p className="layers__note ops-map-layers__soft">
              Practice fleet stays on this computer only and never arms hardware.
            </p>`
          : html`<p className="layers__note ops-map-layers__soft-off" data-cmc-page="page66-soft-off">
              Production map shows live site geometry only.
            </p>`}

        <p className="layers__note">
          A group link is who gets woken together — resolved on the server from
          the notification group. Devices never talk to each other.
        </p>
      </div>
    <//>`;
}

/**
 * Fit Live pin set without locking zoom/bounds. User-only.
 * @returns {boolean}
 */
export function fitMapToDevices(map, devices, centre, opts) {
  if (!map) return false;
  const o = opts || {};
  const pad = o.padding || { top: 72, right: 72, bottom: 96, left: 72 };
  try {
    if (typeof map.setMaxBounds === "function") map.setMaxBounds(null);
    if (typeof map.setMinZoom === "function") map.setMinZoom(OPS_MAP_MIN_ZOOM);
  } catch {
    /* */
  }
  const pts = (devices || [])
    .map((d) => d && d.ll)
    .filter((ll) => ll && ll.length >= 2 && Number.isFinite(Number(ll[0])) && Number.isFinite(Number(ll[1])));
  if (pts.length >= 2) {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const p of pts) {
      minX = Math.min(minX, Number(p[0]));
      maxX = Math.max(maxX, Number(p[0]));
      minY = Math.min(minY, Number(p[1]));
      maxY = Math.max(maxY, Number(p[1]));
    }
    try {
      map.fitBounds(
        [
          [minX, minY],
          [maxX, maxY],
        ],
        {
          padding: pad,
          maxZoom: clampOpsMapZoom(o.maxZoom != null ? o.maxZoom : 14.5),
          duration: o.duration != null ? o.duration : 720,
          essential: true,
        },
      );
      map.__softUserTs = Date.now();
      map.__softFocusHold = false;
      map.__softAiMode = false;
      return true;
    } catch {
      /* fall */
    }
  }
  if (pts.length === 1) {
    frameSoftFocus(map, [Number(pts[0][0]), Number(pts[0][1])], {
      zoom: clampOpsMapZoom(o.singleZoom != null ? o.singleZoom : 14),
      maxZoom: 16.5,
      duration: o.duration != null ? o.duration : 680,
    });
    return true;
  }
  const c = centre && centre.length >= 2 ? centre : null;
  if (c) {
    try {
      map.easeTo({
        center: [Number(c[0]), Number(c[1])],
        zoom: clampOpsMapZoom(o.fallbackZoom != null ? o.fallbackZoom : OPS_MAP_DEFAULT_ZOOM_REGION),
        pitch: 0,
        bearing: 0,
        duration: o.duration != null ? o.duration : 680,
        essential: true,
      });
      map.__softUserTs = Date.now();
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

/** Match Live device roster (id / label) for map search. */
export function findDeviceMapHit(devices, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return null;
  const list = Array.isArray(devices) ? devices : [];
  const scored = [];
  for (const d of list) {
    if (!d) continue;
    const id = String(d.id || d.device_id || "").trim();
    const label = String(d.label || d.name || d.display_label || "").trim();
    const idL = id.toLowerCase();
    const labL = label.toLowerCase();
    let score = 0;
    if (idL === q || labL === q) score = 100;
    else if (idL.startsWith(q) || labL.startsWith(q)) score = 80;
    else if (idL.includes(q) || labL.includes(q)) score = 50;
    else continue;
    const ll = d.ll;
    if (!ll || ll.length < 2) {
      scored.push({ score: score - 5, d, ll: null });
      continue;
    }
    scored.push({ score, d, ll: [Number(ll[0]), Number(ll[1])] });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored[0] || null;
}

/**
 * User place / device jump — never auto; only from search / buttons.
 * @returns {Promise<{ok:boolean, kind?:string, label?:string, error?:string}>}
 */
export async function resolveMapSearchJump(query, devices) {
  const q = String(query || "").trim();
  if (!q) return { ok: false, error: "Enter a place or device name" };
  const pair = parseLatLngPair(q);
  if (pair) {
    return {
      ok: true,
      kind: "coord",
      label: pair.label,
      ll: [pair.lng, pair.lat],
      zoom: 14,
    };
  }
  const dev = findDeviceMapHit(devices, q);
  if (dev && dev.ll) {
    return {
      ok: true,
      kind: "device",
      label: String(dev.d.label || dev.d.id || q),
      deviceId: String(dev.d.id || dev.d.device_id || ""),
      ll: dev.ll,
      zoom: 15.6,
    };
  }
  if (dev && !dev.ll) {
    return { ok: false, error: "That device has no map position yet" };
  }
  try {
    const hit = await geocodeOpenMeteo(q);
    if (!hit) return { ok: false, error: "No place matched that search" };
    return {
      ok: true,
      kind: "place",
      label: hit.label || q,
      ll: [hit.lng, hit.lat],
      zoom: 10.5,
    };
  } catch (_e) {
    return { ok: false, error: "Place search is not available right now" };
  }
}

/**
 * Map control stack — free zoom · search jump · fit Live pins.
 */
export function OpsMapControlsDesk({
  ctl,
  onPanel,
  panelOpen,
  onFocus,
  focusOpen,
  view,
  setView,
  centre,
  devices,
  toast,
  seat,
}) {
  const live = useLive();
  const localSoft = isLocalHost() && !!(seat && seat.softFleetLocalOnly);
  const kpis = mapChromeKpisOf(live);
  const mapOn = seat ? !!seat.mapAllowed : true;
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  /* Fail-closed: parent register/prop only. Shared live bus is not a fleet SoT for Fit/Search. */
  const liveDevices = Array.isArray(devices) ? devices : [];

  const reRead = () => {
    if (seat && !seat.reReadAllowed) {
      toast && toast("Refresh is not available right now.", "warn");
      return;
    }
    if (live && typeof live.reload === "function") live.reload();
    toast && toast("Map data refreshed", "go");
  };

  const onFitPins = () => {
    ctl((m) => {
      if (!m) return;
      const ok = fitMapToDevices(m, liveDevices, centre, {});
      toast && toast(ok ? "Showing map pins · free zoom" : "No pins to frame", ok ? "go" : "warn");
    });
  };

  const onSearch = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (seat && seat.mapAllowed === false) {
      toast && toast("Map sealed for this seat", "warn");
      return;
    }
    setBusy(true);
    try {
      const hit = await resolveMapSearchJump(q, liveDevices);
      if (!hit.ok) {
        toast && toast(hit.error || "Search failed", "warn");
        return;
      }
      ctl((m) => {
        if (!m || !hit.ll) return;
        try {
          if (typeof m.setMaxBounds === "function") m.setMaxBounds(null);
          if (typeof m.setMinZoom === "function") m.setMinZoom(OPS_MAP_MIN_ZOOM);
        } catch {
          /* */
        }
        frameSoftFocus(m, hit.ll, {
          zoom: clampOpsMapZoom(hit.zoom),
          maxZoom: 17.2,
          duration: 780,
        });
        if (hit.kind === "device" && hit.deviceId) {
          m.__softOpenId = "d:" + hit.deviceId;
        }
      });
      toast &&
        toast(
          (hit.kind === "device" ? "Device · " : hit.kind === "place" ? "Place · " : "Point · ") +
            (hit.label || q),
          "go",
        );
    } finally {
      setBusy(false);
    }
  };

  return html`
    <form className="ops-map-search" data-cmc-page="page78-map-search"
      onSubmit=${onSearch} role="search" aria-label="Search place or device">
      <${Ico} d="search" w=${14}/>
      <input type="search" className="ops-map-search__inp" value=${q}
        placeholder="Place or board…"
        disabled=${busy || (seat && seat.mapAllowed === false)}
        onInput=${(e) => setQ(e.target.value)}
        autocomplete="off" />
      <button type="submit" className="ops-map-search__go" disabled=${busy}
        title="Go" aria-label="Go to place or device">${busy ? "…" : "→"}</button>
    </form>
    <div className="mapctl ops-map-ctl"
        data-cmc-page="page78-ctl" data-cmc-residual="page216-ops-map"
        data-cmc-soft-real="map-prop-only">
      <button type="button" onClick=${() => ctl((m) => {
        try {
          if (m && typeof m.setMinZoom === "function") m.setMinZoom(OPS_MAP_MIN_ZOOM);
        } catch { /* */ }
        m && m.zoomIn({ duration: 220 });
      })}
        title="Zoom in"><${Ico} d="plus" w=${15}/></button>
      <button type="button" onClick=${() => ctl((m) => {
        try {
          if (m && typeof m.setMinZoom === "function") m.setMinZoom(OPS_MAP_MIN_ZOOM);
          if (m && typeof m.setMaxBounds === "function") m.setMaxBounds(null);
        } catch { /* */ }
        m && m.zoomOut({ duration: 220 });
      })}
        title="Zoom out · multi-country allowed"><${Ico} d="minus" w=${15}/></button>
      <hr/>
      <button type="button"
        onClick=${() => ctl((m) => m.easeTo({ bearing: 0, pitch: 0, duration: 360 }))}
        title="Reset north / flat"><${Ico} d="compass" w=${15}/></button>
      <button type="button" onClick=${onFitPins}
        title="Frame map pins · free zoom (does not lock map)"><${Ico} d="target" w=${15}/></button>
      <hr/>
      <button type="button" data-on=${view === "globe" ? "1" : "0"}
        onClick=${(e) => { stop(e); setView(view === "globe" ? "site" : "globe"); }}
        title=${view === "globe" ? "Back to map view" : "Pull back to the world"}>
        <${Ico} d="globe" w=${15}/></button>
      ${view === "site" ? html`
        <button type="button" data-on=${focusOpen ? "1" : "0"}
          onClick=${(e) => { stop(e); onFocus && onFocus(); }}
          title="Focus the map on a country, city, district, or road">
          <${Ico} d="target" w=${15}/></button>
        <button type="button" data-on=${panelOpen ? "1" : "0"}
          onClick=${(e) => { stop(e); onPanel(); }}
          title="Layers">
          <${Ico} d="layers" w=${15}/></button>` : null}
      <hr/>
      <button type="button" data-cmc-page="page78-assign"
        onClick=${(e) => {
          stop(e);
          if (seat && !seat.mapAllowed) {
            toast && toast("Map sealed", "warn");
            return;
          }
          let pin = null;
          ctl((m) => { pin = mapCenterPinOf(m); });
          assignExternalPinLive(pin, toast, "map centre");
        }}
        title="Save map centre as External destination">
        <${Ico} d="bolt" w=${15}/></button>
      <button type="button"
        onClick=${(e) => { stop(e); go("/dispatch/robots?from=map_assign"); }}
        title="Open External robots with saved destination">
        <${Ico} d="arrowR" w=${15}/></button>
      <hr/>
      <button type="button" onClick=${reRead} title="Refresh map data"
        data-cmc-reread="map" disabled=${seat && !seat.reReadAllowed}>
        <${Ico} d="refresh" w=${15}/></button>
      <span className="ops-map-ctl__chip" title="Answering / visible"
        data-soft=${localSoft ? "1" : "0"}>
        ${mapOn && kpis.ready ? (kpis.online + "/" + kpis.total) : (mapOn ? "…" : "—")}
      </span>
    </div>`;
}

/** Pure re-export for pin selected compare. */
export { deviceSelIdOf };
